import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { ReviewReply } from "@/components/ReviewReply";
import { CalendarDays, Users, CreditCard, ArrowRight, Search, ShieldCheck, Gift, LayoutGrid, Briefcase, Heart, Star, Quote, CalendarCheck, Sparkles } from "lucide-react";

const stagger = { show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

const FEATURED_CATEGORIES = [
  { label: "HVAC & Repairs", category: "Home Services", img: "/hero/hvac.jpg" },
  { label: "Home & Exterior", category: "Home", img: "/hero/pressure.jpg" },
  { label: "Auto Detailing", category: "Automotive", img: "/hero/detailing.jpg" },
  { label: "Massage & Wellness", category: "Wellness", img: "/hero/massage.jpg" },
  { label: "Personal Training", category: "Fitness", img: "/hero/fitness.jpg" },
  { label: "Barber & Beauty", category: "Beauty", img: "/hero/barber.jpg" },
];

const AVATAR_COLORS = ["bg-primary", "bg-secondary", "bg-amber-600", "bg-emerald-600", "bg-sky-600", "bg-rose-600"];

const HOW_IT_WORKS = [
  { icon: Search, t: "Search", d: "Find a trusted service and provider near you — filter by category, rating and price." },
  { icon: CalendarCheck, t: "Pick a time", d: "See each provider's real, open slots and choose the one that fits your day." },
  { icon: Sparkles, t: "Book & relax", d: "Confirm instantly, message your provider and manage every booking in one place." },
];


const HERO_CATEGORIES = [
  { label: "HVAC & Repairs", img: "/hero/hvac.jpg" },
  { label: "Pressure Washing", img: "/hero/pressure.jpg" },
  { label: "Mobile Car Detailing", img: "/hero/mobile-detail.jpg" },
  { label: "Pet Grooming", img: "/hero/grooming.jpg" },
  { label: "Auto Detailing", img: "/hero/detailing.jpg" },
  { label: "Massage & Wellness", img: "/hero/massage.jpg" },
  { label: "Personal Training", img: "/hero/fitness.jpg" },
  { label: "Barber & Beauty", img: "/hero/barber.jpg" },
];

function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_CATEGORIES.length), 3200);
    return () => clearInterval(t);
  }, []);
  const active = HERO_CATEGORIES[idx];
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
      className="relative border border-border hard-shadow rounded-sm overflow-hidden bg-white" data-testid="hero-carousel">
      <div className="relative w-full h-[420px]">
        <AnimatePresence mode="sync">
          <motion.img
            key={active.img}
            src={active.img}
            alt={active.label}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-carousel-image"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent pointer-events-none" />
        <AnimatePresence mode="wait">
          <motion.div
            key={active.label}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="absolute left-5 bottom-5"
          >
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/80">Popular category</span>
            <p className="font-display text-2xl font-semibold text-white leading-tight" data-testid="hero-carousel-label">{active.label}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="absolute right-4 bottom-5 flex gap-1.5">
        {HERO_CATEGORIES.map((c, i) => (
          <button
            key={c.label}
            onClick={() => setIdx(i)}
            aria-label={`Show ${c.label}`}
            data-testid={`hero-carousel-dot-${i}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
          />
        ))}
      </div>
    </motion.div>
  );
}

function BusinessView() {
  return (
    <div data-testid="business-view">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={item} className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-5">
              SCHEDULING AMPLIFIED
            </motion.p>
            <motion.h1 variants={item} className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[1.05]">
              The Scheduling App that Actually Grows Your Business
            </motion.h1>
            <motion.p variants={item} className="mt-6 text-lg text-muted-foreground max-w-md leading-relaxed">
              Book services, partner with local pros to offer exclusive bundles, and use AI-driven marketing to fill your calendar. We don't just manage your bookings; we find your next customer through co-branding.
            </motion.p>
            <motion.div variants={item} className="mt-8 flex flex-wrap gap-3">
              <Link to="/register"><Button size="lg" className="rounded-sm hard-shadow hard-shadow-hover transition-all hover:-translate-y-[1px]" data-testid="hero-start-btn">
                Start for free <ArrowRight className="w-4 h-4 ml-1" />
              </Button></Link>
              <Link to="/pricing"><Button size="lg" variant="outline" className="rounded-sm" data-testid="hero-pricing-btn">See plans</Button></Link>
            </motion.div>
          </motion.div>

          <HeroCarousel />
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold">Everything you need to run and grow your business.</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {[
            { icon: CalendarDays, t: "Set your availability", d: "Define weekly hours and slot durations. BookdHUB generates bookable time slots automatically." },
            { icon: Users, t: "Let customers book", d: "Customers browse your services, pick an open slot, and confirm — no back-and-forth." },
            { icon: CreditCard, t: "Tiered plans", d: "Grow from free to Pro to unlock more features and advanced tools to keep your business growing" },
          ].map((f, i) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }} className="bg-white border border-border rounded-sm p-8 hard-shadow-hover transition-all">
              <div className="w-11 h-11 bg-secondary flex items-center justify-center rounded-sm mb-5">
                <f.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl font-medium">{f.t}</h3>
              <p className="mt-2 text-muted-foreground leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div className="bg-secondary text-secondary-foreground rounded-sm p-12 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Ready to fill your calendar?</h2>
            <p className="mt-3 text-white/70 max-w-lg">Join as a provider and publish your first service in minutes.</p>
          </div>
          <Link to="/register"><Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 whitespace-nowrap" data-testid="cta-signup-btn">
            Create your account <ArrowRight className="w-4 h-4 ml-1" />
          </Button></Link>
        </div>
      </section>
    </div>
  );
}

function CustomerView() {
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => { api.get("/reviews/featured").then((r) => setTestimonials(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="customer-view">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={item} className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-5">
              BOOK IN SECONDS
            </motion.p>
            <motion.h1 variants={item} className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[1.05]">
              Find trusted local pros and book with a tap
            </motion.h1>
            <motion.p variants={item} className="mt-6 text-lg text-muted-foreground max-w-md leading-relaxed">
              From grooming to repairs to wellness — discover vetted providers near you, see real availability, and lock in your appointment. Booking is always free.
            </motion.p>
            <motion.div variants={item} className="mt-8 flex flex-wrap gap-3">
              <Link to="/browse"><Button size="lg" className="rounded-sm hard-shadow hard-shadow-hover transition-all hover:-translate-y-[1px]" data-testid="customer-browse-btn">
                Find a service <Search className="w-4 h-4 ml-1" />
              </Button></Link>
              <Link to="/register"><Button size="lg" variant="outline" className="rounded-sm" data-testid="customer-signup-btn">Create a free account</Button></Link>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
            className="relative border border-border hard-shadow rounded-sm overflow-hidden bg-white" data-testid="customer-hero-image">
            <div className="relative w-full h-[420px]">
              <img
                src="https://images.unsplash.com/photo-1528123778681-01e39b42808e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Customer booking a service on their phone"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent pointer-events-none" />
              <div className="absolute left-5 bottom-5">
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/80">Booked in 30 seconds</span>
                <p className="font-display text-2xl font-semibold text-white leading-tight">Your next appointment, sorted</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold">Everything you need to book with confidence.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {[
            { icon: ShieldCheck, t: "Trusted local pros", d: "Browse verified providers with real ratings, photos and clear pricing before you book." },
            { icon: Heart, t: "Free to browse & book", d: "No membership, no fees to search or reserve — you only ever pay the provider for the service." },
            { icon: Gift, t: "Bundled packages", d: "Discover multi-visit packages and co-branded bundles that save you money over single visits." },
            { icon: LayoutGrid, t: "All bookings in one place", d: "Track upcoming appointments, message your provider and rebook favourites from one dashboard." },
          ].map((f, i) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }} className="bg-white border border-border rounded-sm p-7 hard-shadow-hover transition-all">
              <div className="w-11 h-11 bg-primary flex items-center justify-center rounded-sm mb-5">
                <f.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-lg font-medium">{f.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured categories */}
      <section className="bg-muted/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold">Popular categories near you.</h2>
            <Link to="/browse" className="text-sm font-semibold text-primary hover:underline" data-testid="customer-view-all-categories">
              View all services →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-10">
            {FEATURED_CATEGORIES.map((c, i) => (
              <motion.div key={c.label} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <Link to={`/browse?category=${encodeURIComponent(c.category)}`} data-testid={`customer-category-${i}`}
                  className="group relative block h-40 rounded-sm overflow-hidden border border-border hard-shadow-hover transition-all">
                  <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <span className="absolute left-4 bottom-3 font-display text-lg font-semibold text-white">{c.label}</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary text-center">How it works</p>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold text-center mt-3">Book in three simple steps.</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {HOW_IT_WORKS.map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }} className="relative bg-white border border-border rounded-sm p-8" data-testid={`customer-step-${i}`}>
              <span className="absolute -top-3 -left-3 w-9 h-9 rounded-sm bg-secondary text-white font-display font-semibold flex items-center justify-center text-sm">{i + 1}</span>
              <div className="w-11 h-11 bg-primary/10 flex items-center justify-center rounded-sm mb-5">
                <s.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl font-medium">{s.t}</h3>
              <p className="mt-2 text-muted-foreground leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials — hidden until there are at least 3 real ones */}
      {testimonials.length >= 3 && (
      <section className="bg-muted/30 border-y border-border" data-testid="customer-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold text-center">Loved by customers everywhere.</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {testimonials.map((t, i) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1 }} className="bg-white border border-border rounded-sm p-7 flex flex-col" data-testid={`customer-testimonial-${i}`}>
                <Quote className="w-7 h-7 text-primary/30" />
                <div className="flex gap-0.5 mt-3">
                  {[...Array(5)].map((_, s) => <Star key={s} className={`w-4 h-4 ${s < t.rating ? "text-primary fill-current" : "text-muted-foreground/40"}`} />)}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground/90 flex-1">“{t.comment}”</p>
                <ReviewReply reply={t.reply} provider={t.subscriber_name} className="mt-3" testid={`customer-testimonial-reply-${i}`} />
                <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border">
                  <span className={`w-10 h-10 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} text-white font-semibold text-sm flex items-center justify-center`}>{(t.customer_name || "C").charAt(0)}</span>
                  <div>
                    <p className="text-sm font-semibold">{t.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{[t.service_title, t.customer_city].filter(Boolean).join(" · ")}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div className="bg-secondary text-secondary-foreground rounded-sm p-12 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Ready to book your next appointment?</h2>
            <p className="mt-3 text-white/70 max-w-lg">Search services near you and reserve a slot in under a minute.</p>
          </div>
          <Link to="/browse"><Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 whitespace-nowrap" data-testid="customer-cta-browse-btn">
            Browse services <ArrowRight className="w-4 h-4 ml-1" />
          </Button></Link>
        </div>
      </section>
    </div>
  );
}

export default function Landing() {
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("bookdhub_home_tab") || "business"; } catch { return "business"; }
  });
  const selectTab = (key) => {
    setTab(key);
    try { localStorage.setItem("bookdhub_home_tab", key); } catch (e) { /* ignore */ }
  };

  const tabs = [
    { key: "business", label: "For businesses looking to grow", icon: Briefcase },
    { key: "customer", label: "For customers looking to book", icon: Search },
  ];

  return (
    <div>
      {/* Audience toggle */}
      <div className="border-b border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex justify-center">
          <div className="inline-flex bg-white border border-border rounded-sm p-1 relative" data-testid="audience-toggle">
            {tabs.map((t) => {
              const activeTab = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => selectTab(t.key)}
                  data-testid={`audience-tab-${t.key}`}
                  className={`relative z-10 flex items-center gap-2 px-4 sm:px-6 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                    activeTab ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {activeTab && (
                    <motion.span
                      layoutId="audience-pill"
                      className="absolute inset-0 bg-primary rounded-sm -z-10"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                  <t.icon className="w-4 h-4" strokeWidth={2} />
                  <span className="hidden sm:inline">{t.label}</span>
                  <span className="sm:hidden">{t.key === "business" ? "Businesses" : "Customers"}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
        >
          {tab === "business" ? <BusinessView /> : <CustomerView />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
